import React, { useState, useEffect, useCallback } from 'react';
import { schedulesApi, formatApiError } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Plus, Trash, Warning, Eye, CalendarX, XCircle } from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';

const TYPE_LABELS = {
  office: 'Ufficio', smart_working: 'Smart Working', leave: 'Ferie/Assenza',
  on_call: 'Reperibilità', permission: 'Permesso', overtime: 'Straordinario', unpaid_leave: 'Assenza Non Retribuita',
};
const TYPE_COLORS = {
  office: '#0A0A0A', smart_working: '#002FA7', leave: '#F59E0B',
  on_call: '#FF2A00', permission: '#8B5CF6', overtime: '#059669', unpaid_leave: '#52525B',
};
const TIME_TYPE_LABELS = { full_day: 'Giornata Intera', partial: 'Parziale', late_entry: 'Entrata Posticipata', early_exit: 'Uscita Anticipata' };
const OVERTIME_LABELS = { ordinary: 'Ordinario', night: 'Notturno', holiday: 'Festivo', holiday_night: 'Festivo + Notturno' };
const DEDUCTION_LABELS = { leave: 'Ferie', rol: 'Permesso Retribuito (ROL)', unpaid: 'Assenza Non Retribuita', none: 'Nessuna' };

const ScheduleBadge = ({ type }) => {
  const color = TYPE_COLORS[type] || '#0A0A0A';
  return <span className="inline-flex items-center px-2 py-0.5 text-xs font-bold uppercase tracking-wider border" style={{ borderColor: color, color, backgroundColor: `${color}10` }}>{TYPE_LABELS[type] || type}</span>;
};
const StatusBadge = ({ status }) => {
  const map = { pending: { c: 'border-[#F59E0B] text-[#F59E0B] bg-[#F59E0B]/5', l: 'In Attesa' }, approved: { c: 'border-[#22C55E] text-[#22C55E] bg-[#22C55E]/5', l: 'Approvata' }, rejected: { c: 'border-[#FF2A00] text-[#FF2A00] bg-[#FF2A00]/5', l: 'Rifiutata' }, waiting: { c: 'border-[#52525B] text-[#52525B] bg-[#52525B]/5', l: 'In Coda' }, cancelled: { c: 'border-[#A1A1AA] text-[#A1A1AA] bg-[#A1A1AA]/5', l: 'Annullata' } };
  const s = map[status] || map.pending;
  return <span className={`inline-flex items-center px-2 py-0.5 text-xs font-bold uppercase tracking-wider border ${s.c}`}>{s.l}</span>;
};

const LUNCH_BREAK = 1; // 1h pausa pranzo
const WORK_DAY_TOTAL = 9; // 8h lavoro + 1h pausa = 9h totale

const calcExitTime = (entryTime) => {
  if (!entryTime) return '';
  const [h, m] = entryTime.split(':').map(Number);
  const exitH = h + WORK_DAY_TOTAL;
  return `${String(exitH).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const MyRequestsPage = () => {
  const { user } = useAuth();
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [form, setForm] = useState({});
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const resetForm = () => {
    setForm({ schedule_type: 'leave', start_date: '', end_date: '', reason: '', notes: '',
      time_type: 'full_day', hours: '', start_time: '', end_time: '',
      deduction_type: '', overtime_type: 'ordinary' });
    setError('');
  };

  const fetchSchedules = useCallback(async () => {
    setLoading(true);
    try { const { data } = await schedulesApi.getAll({ user_id: user?.id }); setSchedules(data); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  }, [user?.id]);

  useEffect(() => { fetchSchedules(); }, [fetchSchedules]);

  const isSingleDay = form.start_date && form.end_date && form.start_date === form.end_date;
  const isPermission = form.schedule_type === 'permission';
  const isOvertime = form.schedule_type === 'overtime';
  const isLateEntry = form.time_type === 'late_entry';
  const isEarlyExit = form.time_type === 'early_exit';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.reason && form.schedule_type !== 'smart_working') {
      setError('La motivazione è obbligatoria');
      return;
    }
    if (isPermission && !form.hours) {
      setError('Indica il numero di ore del permesso');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        schedule_type: form.schedule_type,
        start_date: form.start_date,
        end_date: form.end_date || form.start_date,
        notes: form.notes, reason: form.reason,
        time_type: form.time_type,
        hours: form.hours ? parseFloat(form.hours) : null,
        start_time: form.start_time || null,
        end_time: form.end_time || null,
        deduction_type: form.deduction_type || null,
        overtime_type: isOvertime ? form.overtime_type : null,
      };
      await schedulesApi.create(payload);
      toast.success('Richiesta inviata! In attesa di approvazione.');
      setIsDialogOpen(false);
      resetForm();
      fetchSchedules();
    } catch (err) { setError(formatApiError(err)); } finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Annullare questa richiesta?')) return;
    try { await schedulesApi.delete(id); toast.success('Annullata'); fetchSchedules(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const handleCancelApproved = async () => {
    if (!cancelTarget || !cancelReason.trim()) return;
    setCancelling(true);
    try {
      await schedulesApi.requestCancellation(cancelTarget.id, cancelReason);
      toast.success('Richiesta di annullamento inviata');
      setIsCancelOpen(false);
      setCancelTarget(null);
      setCancelReason('');
      fetchSchedules();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setCancelling(false); }
  };

  const openDetail = async (schedule) => {
    setDetailLoading(true); setIsDetailOpen(true);
    try { const { data } = await schedulesApi.getDetail(schedule.id); setDetailData(data); }
    catch (e) { setDetailData({ schedule, approval: null }); }
    finally { setDetailLoading(false); }
  };

  const myRequests = schedules.filter(s => s.created_by === user?.id || (!s.created_by && s.user_id === user?.id));
  const assignedToMe = schedules.filter(s => s.created_by && s.created_by !== user?.id);

  return (
    <div data-testid="my-requests-page">
      <Toaster position="top-right" richColors />

      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 md:mb-6 gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-[#0A0A0A]">Le Mie Richieste</h1>
          <p className="text-[#52525B] mt-1">Ferie, permessi, smart working e straordinari</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => { resetForm(); setIsDialogOpen(true); }}
              className="bg-[#002FA7] text-white px-6 py-2.5 font-medium hover:bg-[#00278A] rounded-none" data-testid="new-request-btn">
              <Plus size={20} weight="bold" className="mr-2" /> Nuova Richiesta
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg rounded-none border border-[#0A0A0A] max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading text-xl font-bold">Nuova Richiesta</DialogTitle></DialogHeader>
            {error && (
              <div className="bg-[#FF2A00]/5 border border-[#FF2A00] p-3 flex items-start gap-2">
                <Warning className="text-[#FF2A00] flex-shrink-0 mt-0.5" size={16} weight="bold" />
                <p className="text-[#FF2A00] text-sm">{error}</p>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4 mt-2">
              {/* Tipo */}
              <div>
                <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Tipo Richiesta</Label>
                <Select value={form.schedule_type} onValueChange={v => setForm({...form, schedule_type: v, time_type: 'full_day', hours: '', deduction_type: ''})}>
                  <SelectTrigger className="rounded-none border-[#E4E4E7]" data-testid="request-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="leave" className="rounded-none">Ferie / Assenza</SelectItem>
                    <SelectItem value="smart_working" className="rounded-none">Smart Working</SelectItem>
                    <SelectItem value="permission" className="rounded-none">Permesso</SelectItem>
                    <SelectItem value="overtime" className="rounded-none">Straordinario</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Date */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Data Inizio</Label>
                  <Input type="date" value={form.start_date}
                    onChange={e => setForm({...form, start_date: e.target.value, end_date: form.end_date || e.target.value})}
                    className="rounded-none border-[#E4E4E7]" data-testid="start-date-input" required />
                </div>
                <div>
                  <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Data Fine</Label>
                  <Input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})}
                    className="rounded-none border-[#E4E4E7]" data-testid="end-date-input" required />
                </div>
              </div>

              {/* === PERMESSO: ore + tipo orario === */}
              {isPermission && (
                <div className="bg-[#8B5CF6]/5 border border-[#8B5CF6]/30 p-4 space-y-4">
                  <p className="text-xs font-bold uppercase tracking-wider text-[#8B5CF6]">Dettagli Permesso</p>
                  <div>
                    <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Ore di Permesso *</Label>
                    <Input type="number" step="0.5" min="0.5" max="8" value={form.hours}
                      onChange={e => setForm({...form, hours: e.target.value})}
                      className="rounded-none border-[#E4E4E7] w-32" placeholder="Es: 2" data-testid="hours-input" required />
                  </div>
                  {isSingleDay && (
                    <>
                      <div>
                        <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Tipo</Label>
                        <Select value={form.time_type} onValueChange={v => setForm({...form, time_type: v})}>
                          <SelectTrigger className="rounded-none border-[#E4E4E7]" data-testid="time-type-select"><SelectValue /></SelectTrigger>
                          <SelectContent className="rounded-none">
                            <SelectItem value="late_entry" className="rounded-none">Entrata Posticipata</SelectItem>
                            <SelectItem value="early_exit" className="rounded-none">Uscita Anticipata</SelectItem>
                            <SelectItem value="partial" className="rounded-none">Permesso Ore (generico)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {isLateEntry && (
                        <div className="space-y-3">
                          <div>
                            <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Orario Entrata</Label>
                            <Input type="time" value={form.start_time}
                              onChange={e => setForm({...form, start_time: e.target.value, end_time: calcExitTime(e.target.value)})}
                              className="rounded-none border-[#E4E4E7] w-40" data-testid="start-time-input" />
                          </div>
                          {form.start_time && (
                            <p className="text-sm text-[#52525B]">
                              Uscita prevista: <b>{calcExitTime(form.start_time)}</b> (8h lavoro + 1h pausa pranzo)
                            </p>
                          )}
                        </div>
                      )}

                      {isEarlyExit && (
                        <div className="space-y-3">
                          <div>
                            <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Orario Uscita Desiderato</Label>
                            <Input type="time" value={form.end_time}
                              onChange={e => setForm({...form, end_time: e.target.value})}
                              className="rounded-none border-[#E4E4E7] w-40" data-testid="end-time-input" />
                          </div>
                          <div>
                            <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">
                              Come vuoi gestire le ore non lavorate?
                            </Label>
                            <Select value={form.deduction_type} onValueChange={v => setForm({...form, deduction_type: v})}>
                              <SelectTrigger className="rounded-none border-[#E4E4E7]" data-testid="deduction-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                              <SelectContent className="rounded-none">
                                <SelectItem value="rol" className="rounded-none">Permesso Retribuito (ROL)</SelectItem>
                                <SelectItem value="leave" className="rounded-none">Ferie</SelectItem>
                                <SelectItem value="unpaid" className="rounded-none">Assenza Non Retribuita</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* === STRAORDINARIO === */}
              {isOvertime && (
                <div className="bg-[#059669]/5 border border-[#059669]/30 p-4 space-y-4">
                  <p className="text-xs font-bold uppercase tracking-wider text-[#059669]">Dettagli Straordinario</p>
                  <div>
                    <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Tipo Straordinario</Label>
                    <Select value={form.overtime_type} onValueChange={v => setForm({...form, overtime_type: v})}>
                      <SelectTrigger className="rounded-none border-[#E4E4E7]" data-testid="overtime-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="rounded-none">
                        <SelectItem value="ordinary" className="rounded-none">Ordinario</SelectItem>
                        <SelectItem value="night" className="rounded-none">Notturno</SelectItem>
                        <SelectItem value="holiday" className="rounded-none">Festivo</SelectItem>
                        <SelectItem value="holiday_night" className="rounded-none">Festivo + Notturno</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Ore di Straordinario *</Label>
                    <Input type="number" step="0.5" min="0.5" max="12" value={form.hours}
                      onChange={e => setForm({...form, hours: e.target.value})}
                      className="rounded-none border-[#E4E4E7] w-32" placeholder="Es: 3" required />
                  </div>
                  <p className="text-xs text-[#52525B]">Lo straordinario verrà approvato dal Team Leader.</p>
                </div>
              )}

              {/* Motivazione */}
              <div>
                <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">
                  Motivazione {form.schedule_type !== 'smart_working' ? '*' : '(Facoltativo)'}
                </Label>
                <Textarea value={form.reason || ''} onChange={e => setForm({...form, reason: e.target.value})}
                  placeholder={form.schedule_type === 'leave' ? 'Es: Ferie programmate, visita medica...' :
                    form.schedule_type === 'permission' ? 'Es: Visita medica, impegno personale...' :
                    form.schedule_type === 'overtime' ? 'Es: Scadenza progetto, rilascio urgente...' :
                    'Es: Concentrazione, lavoro da remoto...'}
                  className="rounded-none border-[#E4E4E7] min-h-16" data-testid="reason-input"
                  required={form.schedule_type !== 'smart_working'} />
              </div>

              {/* Note */}
              <div>
                <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Note (Facoltativo)</Label>
                <Textarea value={form.notes || ''} onChange={e => setForm({...form, notes: e.target.value})}
                  placeholder="Note aggiuntive..." className="rounded-none border-[#E4E4E7] min-h-12" data-testid="notes-input" />
              </div>

              <p className="text-xs text-[#52525B] border-t border-[#E4E4E7] pt-3">
                {isOvertime ? 'Lo straordinario verrà approvato dal Team Leader.' : 'La richiesta verrà approvata dal Team Leader e/o dal Manager.'}
              </p>

              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="flex-1 rounded-none border-[#0A0A0A]">Annulla</Button>
                <Button type="submit" disabled={submitting} className="flex-1 bg-[#002FA7] text-white hover:bg-[#00278A] rounded-none" data-testid="submit-request-btn">
                  {submitting ? 'Invio...' : 'Invia Richiesta'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Turni Assegnati */}
      {assignedToMe.length > 0 && (
        <div className="mb-8">
          <h2 className="font-heading text-xl font-bold tracking-tight mb-4">Turni Assegnati</h2>
          <div className="border border-[#E4E4E7] overflow-x-auto">
            <table className="w-full data-table min-w-[700px]">
              <thead><tr className="border-b-2 border-[#0A0A0A] bg-white">
                <th className="py-4 px-4 text-left">Tipo</th><th className="py-4 px-4 text-left">Periodo</th>
                <th className="py-4 px-4 text-left">Assegnato da</th><th className="py-4 px-4 text-left">Note</th>
              </tr></thead>
              <tbody>
                {assignedToMe.map(s => (
                  <tr key={s.id} className="border-b border-[#E4E4E7] hover:bg-[#F4F4F5]">
                    <td className="py-3 px-4"><ScheduleBadge type={s.schedule_type} /></td>
                    <td className="py-3 px-4 font-mono text-sm">{s.start_date} → {s.end_date}</td>
                    <td className="py-3 px-4 text-sm">{s.created_by_name || '-'}</td>
                    <td className="py-3 px-4 text-sm text-[#52525B] truncate max-w-xs">{s.notes || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Le Mie Richieste */}
      <h2 className="font-heading text-xl font-bold tracking-tight mb-4">Le Mie Richieste</h2>
      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#0A0A0A] border-t-transparent rounded-full animate-spin"></div></div>
      ) : myRequests.length === 0 ? (
        <div className="border border-[#E4E4E7] p-12 text-center">
          <CalendarX size={48} className="mx-auto text-[#E4E4E7] mb-4" />
          <h3 className="font-heading font-bold text-lg mb-2">Nessuna richiesta</h3>
          <p className="text-[#52525B] mb-4">Non hai ancora inviato richieste.</p>
        </div>
      ) : (
        <div className="border border-[#E4E4E7] overflow-x-auto">
          <table className="w-full data-table min-w-[700px]">
            <thead><tr className="border-b-2 border-[#0A0A0A] bg-white">
              <th className="py-4 px-4 text-left">Tipo</th><th className="py-4 px-4 text-left">Periodo</th>
              <th className="py-4 px-4 text-left">Dettaglio</th><th className="py-4 px-4 text-left">Ore</th>
              <th className="py-4 px-4 text-left">Stato</th><th className="py-4 px-4 text-left">Motivazione</th>
              <th className="py-4 px-4 text-right">Azioni</th>
            </tr></thead>
            <tbody>
              {myRequests.map(s => (
                <tr key={s.id} className="border-b border-[#E4E4E7] hover:bg-[#F4F4F5]" data-testid={`request-row-${s.id}`}>
                  <td className="py-3 px-4"><ScheduleBadge type={s.schedule_type} /></td>
                  <td className="py-3 px-4 font-mono text-xs">{s.start_date}{s.end_date !== s.start_date ? ` → ${s.end_date}` : ''}</td>
                  <td className="py-3 px-4 text-xs">
                    {s.schedule_type === 'overtime' ? (OVERTIME_LABELS[s.overtime_type] || '') :
                     s.time_type !== 'full_day' ? TIME_TYPE_LABELS[s.time_type] : 'Giornata Intera'}
                    {s.start_time && <span className="text-[#52525B] ml-1">{s.start_time}-{s.end_time}</span>}
                  </td>
                  <td className="py-3 px-4 font-mono text-sm font-bold">{s.hours || 0}h</td>
                  <td className="py-3 px-4"><StatusBadge status={s.status} /></td>
                  <td className="py-3 px-4 text-sm text-[#52525B] max-w-[180px] truncate">{s.reason || '-'}</td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => openDetail(s)} className="w-8 h-8 flex items-center justify-center border border-[#E4E4E7] hover:border-[#0A0A0A] transition-colors" data-testid={`view-request-${s.id}`} title="Dettagli"><Eye size={16} weight="bold" /></button>
                      {s.status === 'pending' && (
                        <button onClick={() => handleDelete(s.id)} className="w-8 h-8 flex items-center justify-center border border-[#E4E4E7] hover:border-[#FF2A00] hover:text-[#FF2A00] transition-colors" data-testid={`cancel-request-${s.id}`} title="Annulla"><Trash size={16} weight="bold" /></button>
                      )}
                      {s.status === 'approved' && !s.cancellation_requested && (
                        <button onClick={() => { setCancelTarget(s); setCancelReason(''); setIsCancelOpen(true); }}
                          className="w-8 h-8 flex items-center justify-center border border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B] hover:text-white transition-colors"
                          data-testid={`cancel-approved-${s.id}`} title="Richiedi Annullamento">
                          <XCircle size={16} weight="bold" />
                        </button>
                      )}
                      {s.cancellation_requested && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-[#F59E0B] border border-[#F59E0B] px-2 py-1 bg-[#F59E0B]/5">Annullamento richiesto</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="sm:max-w-2xl rounded-none border border-[#0A0A0A] max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-xl font-bold">Dettagli Richiesta</DialogTitle></DialogHeader>
          {detailLoading ? (
            <div className="flex items-center justify-center h-32"><div className="w-8 h-8 border-2 border-[#0A0A0A] border-t-transparent rounded-full animate-spin"></div></div>
          ) : detailData && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-1">Tipo</p><ScheduleBadge type={detailData.schedule.schedule_type} /></div>
                <div><p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-1">Stato</p><StatusBadge status={detailData.schedule.status} /></div>
                <div><p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-1">Data Inizio</p><p className="font-mono font-bold">{detailData.schedule.start_date}</p></div>
                <div><p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-1">Data Fine</p><p className="font-mono font-bold">{detailData.schedule.end_date}</p></div>
                <div><p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-1">Ore</p><p className="font-mono font-bold text-[#002FA7]">{detailData.schedule.hours || 0}h</p></div>
                {detailData.schedule.overtime_type && (
                  <div><p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-1">Tipo Straordinario</p><p>{OVERTIME_LABELS[detailData.schedule.overtime_type]}</p></div>
                )}
                {detailData.schedule.time_type !== 'full_day' && (
                  <div><p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-1">Tipo Orario</p><p>{TIME_TYPE_LABELS[detailData.schedule.time_type]}</p></div>
                )}
                {detailData.schedule.start_time && (
                  <div><p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-1">Orario</p><p className="font-mono">{detailData.schedule.start_time} - {detailData.schedule.end_time}</p></div>
                )}
                {detailData.schedule.deduction_type && (
                  <div><p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-1">Deduzione</p><p>{DEDUCTION_LABELS[detailData.schedule.deduction_type]}</p></div>
                )}
              </div>
              {detailData.schedule.reason && (
                <div className="border border-[#E4E4E7] p-4 bg-[#FAFAFA]">
                  <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-1">Motivazione</p>
                  <p className="text-sm">{detailData.schedule.reason}</p>
                </div>
              )}
              {detailData.schedule.notes && (
                <div><p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-1">Note</p><p className="text-sm">{detailData.schedule.notes}</p></div>
              )}
              {/* Approval Progress */}
              {detailData.approval && (
                <div className="border-t border-[#E4E4E7] pt-4">
                  <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-3">Stato Approvazione</p>
                  <div className="space-y-2">
                    {detailData.approval.approval_levels?.map((level, idx) => (
                      <div key={idx} className="flex items-center justify-between py-3 px-4 bg-white border border-[#E4E4E7]">
                        <div>
                          <span className="font-medium">{level.level === 'team_leader' ? 'Team Leader' : 'Manager'}</span>
                          {level.approver_name && <span className="text-xs text-[#52525B] ml-2">({level.approver_name})</span>}
                        </div>
                        <div className="flex items-center gap-3">
                          <StatusBadge status={level.status} />
                          {level.approved_at && <span className="text-xs text-[#52525B]">{new Date(level.approved_at).toLocaleDateString('it-IT')}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                  {detailData.approval.approval_levels?.filter(l => l.comments).map((l, i) => (
                    <div key={i} className="mt-2 p-3 bg-[#FAFAFA] border border-[#E4E4E7]">
                      <p className="text-xs text-[#52525B] mb-1"><b>{l.level === 'team_leader' ? 'Team Leader' : 'Manager'}:</b></p>
                      <p className="text-sm">{l.comments}</p>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-[#52525B] pt-2 border-t border-[#E4E4E7]">Inviata il {new Date(detailData.schedule.created_at).toLocaleString('it-IT')}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Cancel Approved Request Dialog */}
      <Dialog open={isCancelOpen} onOpenChange={setIsCancelOpen}>
        <DialogContent className="sm:max-w-md rounded-none border border-[#0A0A0A]">
          <DialogHeader><DialogTitle className="font-heading text-xl font-bold">Richiedi Annullamento</DialogTitle></DialogHeader>
          {cancelTarget && (
            <div className="space-y-4 mt-4">
              <div className="bg-[#FAFAFA] border border-[#E4E4E7] p-4">
                <p className="text-sm font-medium">{TYPE_LABELS[cancelTarget.schedule_type]}</p>
                <p className="text-sm text-[#52525B] font-mono">{cancelTarget.start_date} → {cancelTarget.end_date}</p>
              </div>
              <div className="bg-[#F59E0B]/5 border border-[#F59E0B] p-3 flex items-start gap-2">
                <Warning className="text-[#F59E0B] flex-shrink-0 mt-0.5" size={16} weight="bold" />
                <p className="text-sm text-[#F59E0B]">La richiesta di annullamento dovrà essere approvata dal Manager.</p>
              </div>
              <div>
                <Label className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">Motivazione Annullamento *</Label>
                <Textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)}
                  placeholder="Indica il motivo dell'annullamento..."
                  className="rounded-none border-[#E4E4E7] min-h-20" data-testid="cancel-reason-input" required />
              </div>
              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setIsCancelOpen(false)} className="flex-1 rounded-none border-[#0A0A0A]">Chiudi</Button>
                <Button type="button" disabled={cancelling || !cancelReason.trim()} onClick={handleCancelApproved}
                  className="flex-1 bg-[#F59E0B] text-white hover:bg-[#D97706] rounded-none" data-testid="confirm-cancel-btn">
                  {cancelling ? 'Invio...' : 'Richiedi Annullamento'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyRequestsPage;
